import { AudioEngine, AudioMixer } from '@tunasong/audio-ui'
import { ConfigService } from '@tunasong/redux'
import { ErrorMessage, Profile, RootMain } from '@tunasong/tunasong-ui'
import { createBrowserRouter, SuspenseBusy } from '@tunasong/ui-lib'
import AppContainer from './app-container.js'
import {
  LazyEmbed,
  LazyErrorBoundary,
  LazyIndexPage,
  LazyLabs,
  LazyLogin,
  LazyLogout,
  LazyPage,
  LazyTermsOfService,
  LazyWaitlistPage,
} from './lazy-routes.js'
import { pageChrome } from './page-chrome.js'
import { FavoritiesPage } from './pages/favorities.js'
import { RouterAppChrome } from './router-entity-container.js'
import { RouterAppGroupChrome } from './router-entity-group-container.js'
import { docsRouter } from './docs/docs-routes.js'
import { MusicTheoryPage } from './pages/music-theory-page.js'
import { GuitarPatternsPage } from './pages/guitar-patterns-page.js'
import { GuitarChordsPage } from './pages/guitar-chords-page.js'
import { ExternalToolPage } from './pages/external-tool-page.js'
import { GamesPage } from './pages/games.js'

/**
 * @todo check auth https://www.robinwieruch.de/react-router-authentication/
 * @todo we can use the route loader to load the entities
 * @todo implement basename
 * */

const errorElement = <LazyErrorBoundary />

const configService = new ConfigService()
const audioEngine = new AudioEngine({ configService })

const router = createBrowserRouter(
  [
    { path: '', element: <LazyIndexPage />, index: true },

    {
      path: 'error',
      children: [
        {
          path: '404',
          element: <ErrorMessage message="Page is not found" />,
        },
      ],
    },
    {
      path: 'games/caged',
      element: (
        <AppContainer
          audioEngine={audioEngine}
          configService={configService}
          component={GamesPage}
          audio={true}
          auth={false}
          theme="dark"
        />
      ),
    },

    { path: 'terms', element: <LazyTermsOfService /> },
    { path: 'waitlist', element: <LazyWaitlistPage /> },
    /** Public app paths */
    {
      path: 'embed',
      element: (
        <AppContainer
          audioEngine={audioEngine}
          configService={configService}
          component={LazyEmbed}
          audio={true}
          theme="light"
        />
      ),
    },
    {
      path: 'page/:slug',
      element: <AppContainer audioEngine={audioEngine} configService={configService} component={LazyPage} />,
    },

    /** Main app below */
    {
      path: 'app/',
      errorElement,
      children: [
        {
          path: '',
          index: true,
          element: (
            <AppContainer
              audioEngine={audioEngine}
              configService={configService}
              component={pageChrome(RootMain, {
                chromeProps: {
                  pageTitle: 'Dashboard',
                },
              })}
              audio={true}
            />
          ),
        },
        {
          path: 'login',
          element: (
            <AppContainer audioEngine={audioEngine} configService={configService} component={LazyLogin} auth={false} />
          ),
        },
        {
          path: 'logout',
          element: (
            <AppContainer audioEngine={audioEngine} configService={configService} component={LazyLogout} auth={false} />
          ),
        },
        {
          path: 'e/:entityId/*',
          element: (
            <AppContainer
              audioEngine={audioEngine}
              configService={configService}
              component={RouterAppChrome}
              audio={true}
            />
          ),
          HydrateFallback: SuspenseBusy,
        },
        {
          // group - still in development for groups / tabs
          path: 'g/:entityId/*',
          element: (
            <AppContainer
              audioEngine={audioEngine}
              configService={configService}
              component={RouterAppGroupChrome}
              audio={true}
            />
          ),
          HydrateFallback: SuspenseBusy,
        },
        {
          path: 'mixer',
          element: (
            <AppContainer
              audioEngine={audioEngine}
              configService={configService}
              component={pageChrome(AudioMixer, {
                container: true,
                containerProps: {
                  style: { paddingTop: 16 },
                },
              })}
              audio={true}
            />
          ),
        },
        {
          path: 'profile',
          element: (
            <AppContainer
              audioEngine={audioEngine}
              configService={configService}
              component={pageChrome(Profile, { container: true })}
              audio={true}
            />
          ),
        },
        {
          path: 'favorites',
          element: (
            <AppContainer
              audioEngine={audioEngine}
              configService={configService}
              component={pageChrome(FavoritiesPage, { container: true })}
              audio={true}
            />
          ),
        },

        {
          path: 'labs',
          element: (
            <AppContainer
              audioEngine={audioEngine}
              configService={configService}
              component={pageChrome(LazyLabs, {
                container: false,
                chromeProps: { pageTitle: 'Labs', pageIconName: 'Labs' },
              })}
              audio={true}
            />
          ),
        },
        {
          path: 'theory',
          element: (
            <AppContainer
              audioEngine={audioEngine}
              configService={configService}
              component={pageChrome(MusicTheoryPage, {
                container: false,
                chromeProps: { pageTitle: 'Music Theory', pageIconName: 'MusicNote' },
              })}
              audio={true}
            />
          ),
        },
        {
          path: 'guitarchords',
          element: (
            <AppContainer
              audioEngine={audioEngine}
              configService={configService}
              component={pageChrome(GuitarChordsPage, {
                chromeProps: { pageTitle: 'Guitar Chords', pageIconName: 'ElectricGuitar' },
                container: true,
              })}
              audio={true}
            />
          ),
        },
        {
          path: 'guitarpatterns',
          element: (
            <AppContainer
              audioEngine={audioEngine}
              configService={configService}
              component={pageChrome(GuitarPatternsPage, {
                container: true,
                chromeProps: {
                  showPageHeader: true,
                  pageTitle: 'Guitar Patterns',
                  pageDescription: 'Guitar patterns and shapes',
                  pageIconName: 'Fretboard',
                },
              })}
              audio={true}
            />
          ),
        },

        {
          path: 'external-tool',
          element: (
            <AppContainer
              audioEngine={audioEngine}
              configService={configService}
              component={pageChrome(ExternalToolPage, {
                container: false,
                chromeProps: { pageTitle: 'External Tool', pageIconName: 'OpenLink' },
              })}
            />
          ),
        },

        // @deprecated - don't use below
        {
          path: ':entityType/:entityId/*',
          element: (
            <AppContainer
              audioEngine={audioEngine}
              configService={configService}
              component={RouterAppChrome}
              audio={true}
            />
          ),
        },
        {
          path: 'entities/:entityId/*',
          element: (
            <AppContainer
              audioEngine={audioEngine}
              configService={configService}
              component={RouterAppChrome}
              audio={true}
            />
          ),
        },
      ],
    },

    /** The docs router */
    ...docsRouter,

    { path: '*', element: <ErrorMessage message="Page is not found" /> },
  ],
  {
    /** So we can omit all /app/* in Links */
    // basename: '/app',
  }
)

export default router
